import type { StoreCapabilities } from "@hobbyshopify/backend-client";
import { ChangeNotifier } from "subtree.js/core";
import type { LoadStatus } from "../primitives/load-status";

/**
 * What HobbyScan staff have switched on for the selected shop, and what it has
 * asked for. Keyed by the shop, so a selection change empties it; the shell
 * loads it, the request flow replaces it with the server's answer.
 */
export class CapabilitiesRepository extends ChangeNotifier {
  #storeId: string | null = null;
  #status: LoadStatus = { kind: "idle" };
  #capabilities: StoreCapabilities | null = null;

  get storeId(): string | null {
    return this.#storeId;
  }

  get status(): LoadStatus {
    return this.#status;
  }

  /** The selected shop's answer, or null until it arrives. */
  get capabilities(): StoreCapabilities | null {
    return this.#capabilities;
  }

  /** The answer for this exact shop, or null when there is none yet. */
  for(storeId: string): StoreCapabilities | null {
    return this.#storeId === storeId ? this.#capabilities : null;
  }

  setLoading(storeId: string): void {
    if (this.#storeId !== storeId) this.#capabilities = null;
    this.#storeId = storeId;
    this.#set({ kind: "loading" });
  }

  setLoaded(storeId: string, capabilities: StoreCapabilities): void {
    this.#storeId = storeId;
    this.#capabilities = capabilities;
    this.#set({ kind: "loaded" });
  }

  setError(storeId: string, message: string): void {
    if (this.#storeId !== storeId) this.#capabilities = null;
    this.#storeId = storeId;
    this.#set({ kind: "error", message });
  }

  reset(): void {
    this.#storeId = null;
    this.#capabilities = null;
    this.#set({ kind: "idle" });
  }

  #set(status: LoadStatus): void {
    this.#status = status;
    this.notifyListeners();
  }
}
